import clsx from 'clsx'
import { useEffect, useState, type ReactNode } from 'react'
import { FilterChips } from '../../cc'
import { MultiSelectChips, type MultiSelectChipOption } from '../../cc/MultiSelectChips'
import { localInputValueToMs, msToLocalInputValue } from '../../lib/localDateTime'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { DriverSearchField } from '../DriverSearchField'
import { VehicleSearchField } from '../VehicleSearchField'
import { reportInput, reportSegment, reportSegmentBtn } from '../ui'
import {
  clearNarrowing, isNarrowed, presetWindow, SITE_LABEL,
  type AlertFilters, type AlertPreset, type ShiftFilter,
} from './alertFilters'
import { kindLabel, SEVERITY_LABEL } from './alertLabels'
import { ALERT_SEVERITIES, type AlertSite, type FacetsEnvelope } from './alertTypes'

export interface AlertFiltersBarProps {
  filters: AlertFilters
  onChange: (patch: Partial<AlertFilters>) => void
  /** The sites this user may switch between; a single site hides the pills entirely. */
  sites: readonly AlertSite[]
  /** Window-wide counts for the kind/severity/hub pickers; null while loading. */
  facets: FacetsEnvelope | null
  /** Right-hand slot -- "Export this view" lives here so it sits with the controls. */
  actions?: ReactNode
  /** True when the facets request failed: say so instead of "Loading…" forever. */
  facetsFailed?: boolean
}

const PRESETS: { id: AlertPreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'Custom' },
]

const SHIFTS: { id: ShiftFilter; label: string }[] = [
  { id: '', label: 'All hours' },
  { id: 'day', label: 'Day' },
  { id: 'night', label: 'Night' },
]

const FIELD_LABEL = 'flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500'

/**
 * The one filter bar every sub-tab shares. Pure controlled component: it reads
 * `filters` and emits patches; the shell owns the URL. The two free-text fields
 * (driver, vehicle) keep their own keystroke state and publish it debounced so the
 * address bar -- and the fetch behind it -- changes once per pause, not per character.
 */
export function AlertFiltersBar({ filters, onChange, sites, facets, actions, facetsFailed = false }: AlertFiltersBarProps): JSX.Element {
  const [driverText, setDriverText] = useState(filters.driver)
  const [vehicleText, setVehicleText] = useState(filters.vehicle)
  const debouncedDriver = useDebouncedValue(driverText)
  const debouncedVehicle = useDebouncedValue(vehicleText)

  // Outside changes (Clear filters, a row click cross-filtering, a pasted URL) must land
  // in the text boxes too, or the box would keep showing a value the report no longer uses.
  useEffect(() => { setDriverText(filters.driver) }, [filters.driver])
  useEffect(() => { setVehicleText(filters.vehicle) }, [filters.vehicle])
  useEffect(() => {
    if (debouncedDriver !== filters.driver) onChange({ driver: debouncedDriver })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDriver])
  useEffect(() => {
    if (debouncedVehicle !== filters.vehicle) onChange({ vehicle: debouncedVehicle })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedVehicle])

  const severityCounts = new Map((facets?.severities ?? []).map((f) => [f.value, f.n]))
  const kindOptions: MultiSelectChipOption[] = (facets?.kinds ?? [])
    .slice()
    .sort((a, b) => b.n - a.n)
    .map((f) => ({ id: f.value, label: kindLabel(f.value), count: f.n }))
  // A kind already picked but absent from this window's facets stays visible/clearable.
  for (const k of filters.kind) if (!kindOptions.some((o) => o.id === k)) kindOptions.push({ id: k, label: kindLabel(k) })
  const hubOptions = (facets?.hubs ?? []).slice().sort((a, b) => b.n - a.n)

  const onPreset = (p: AlertPreset) => {
    if (p === 'custom') {
      onChange({ preset: 'custom' })
      return
    }
    const w = presetWindow(p)!
    onChange({ preset: p, from: w.from, to: w.to })
  }

  const narrowed = isNarrowed(filters)

  return (
    <div className="rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/70 p-3.5 shadow-sm" aria-label="Alert report filters" role="region">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {sites.length > 1 ? (
          <div className={FIELD_LABEL}>
            site
            <FilterChips
              options={sites.map((s) => ({ id: s, label: SITE_LABEL[s] }))}
              active={filters.site}
              onPick={(id) => onChange({ site: id as AlertSite })}
            />
          </div>
        ) : null}

        <div className={FIELD_LABEL}>
          window
          <div className={reportSegment} role="group" aria-label="Date range preset">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={filters.preset === p.id}
                onClick={() => onPreset(p.id)}
                className={clsx(reportSegmentBtn(filters.preset === p.id), '!px-2.5 !py-1 !text-[12px]')}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {filters.preset === 'custom' ? (
          <>
            <label className={FIELD_LABEL}>
              from
              <input
                type="datetime-local"
                value={msToLocalInputValue(filters.from)}
                onChange={(e) => {
                  const ms = localInputValueToMs(e.target.value)
                  if (ms !== null && ms < filters.to) onChange({ from: ms })
                }}
                className={clsx(reportInput, 'font-mono')}
              />
            </label>
            <label className={FIELD_LABEL}>
              to
              <input
                type="datetime-local"
                value={msToLocalInputValue(filters.to)}
                onChange={(e) => {
                  const ms = localInputValueToMs(e.target.value)
                  if (ms !== null && ms > filters.from) onChange({ to: ms })
                }}
                className={clsx(reportInput, 'font-mono')}
              />
            </label>
          </>
        ) : null}

        <DriverSearchField value={driverText} onChange={setDriverText} />
        <VehicleSearchField value={vehicleText} onChange={setVehicleText} />

        <label className={FIELD_LABEL}>
          hub / place
          <select
            value={filters.hub}
            onChange={(e) => onChange({ hub: e.target.value })}
            className={clsx(reportInput, 'min-w-[140px]')}
          >
            <option value="">all places</option>
            {hubOptions.map((h) => (
              <option key={h.value} value={h.value}>{h.value} ({h.n.toLocaleString('en-IN')})</option>
            ))}
            {filters.hub && !hubOptions.some((h) => h.value === filters.hub) ? (
              <option value={filters.hub}>{filters.hub}</option>
            ) : null}
          </select>
        </label>

        <div className={FIELD_LABEL}>
          shift
          <div className={reportSegment} role="group" aria-label="Shift">
            {SHIFTS.map((s) => (
              <button
                key={s.id || 'all'}
                type="button"
                aria-pressed={filters.shift === s.id}
                onClick={() => onChange({ shift: s.id })}
                className={clsx(reportSegmentBtn(filters.shift === s.id), '!px-2.5 !py-1 !text-[12px]')}
                title={s.id === 'night' ? '22:00–06:00 IST' : s.id === 'day' ? '06:00–22:00 IST' : undefined}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="ml-auto flex items-end gap-2">
          {narrowed ? (
            <button
              type="button"
              onClick={() => onChange(clearNarrowing(filters))}
              className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
            >
              Clear filters
            </button>
          ) : null}
          {actions}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-start gap-x-6 gap-y-2 border-t border-slate-200/70 pt-3">
        <div className={FIELD_LABEL}>
          severity
          <MultiSelectChips
            label="Severity"
            options={ALERT_SEVERITIES.map((s) => ({ id: s, label: SEVERITY_LABEL[s], count: severityCounts.get(s) }))}
            value={filters.severity}
            onChange={(severity) => onChange({ severity })}
          />
        </div>
        <div className={clsx(FIELD_LABEL, 'min-w-0 flex-1')}>
          alert type
          {kindOptions.length ? (
            <MultiSelectChips
              label="Alert type"
              options={kindOptions}
              value={filters.kind}
              onChange={(kind) => onChange({ kind })}
              max={10}
            />
          ) : (
            <span className="text-[11px] font-normal normal-case tracking-normal text-slate-400">
              {facets ? 'No alert types in this window.' : facetsFailed ? 'Alert types unavailable — the reporting server did not answer.' : 'Loading alert types…'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
